import api from './axios';

const AUTH_URL = '/api/v1/auth';
const API_URL = '/api/v1';

export const auth = {
  login: (credentials) => api.post(`${AUTH_URL}/login`, credentials),
  register: (userData) => api.post(`${AUTH_URL}/register`, userData),
  logout: () => api.delete(`${AUTH_URL}/logout`),
  requestPasswordReset: (data) => api.post(`${AUTH_URL}/password`, data),
  resetPassword: (data) => api.patch(`${AUTH_URL}/password`, data),
};

export const organizations = {
  // Public — validate that a subdomain exists and is active
  validate: (subdomain) => api.get(`${API_URL}/organizations/validate`, { params: { subdomain } }),
};

export const invitation = {
  // Get invitation details by token
  getInvitation: (token) => api.get(`${AUTH_URL}/invitation`, { params: { token } }),
  // Accept invitation (set password)
  acceptInvitation: (userData) => api.put(`${AUTH_URL}/invitation`, { user: userData }),
};

export const profile = {
  // Get current user profile details
  getProfile: () => api.get(`${API_URL}/profile/me`),
  // Complete profile after invitation acceptance
  completeProfile: (profileData) => api.patch(
    `${API_URL}/profile/complete`,
    { user: profileData }
  ),
  // Upload profile picture
  uploadPicture: (file) => {
    const formData = new FormData();
    formData.append('profile_picture', file);
    return api.patch(`${API_URL}/profile/upload_picture`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Remove profile picture
  removePicture: () => api.delete(`${API_URL}/profile/remove_picture`),
  getNotificationPrefs: () => api.get(`${API_URL}/profile/notifications`),
  updateNotificationPrefs: (preferences) =>
    api.patch(`${API_URL}/profile/notifications`, { preferences }),
};

// Bank Account management
export const bankAccounts = {
  list: (userId) => api.get(`${API_URL}/users/${userId}/bank_accounts`),
  create: (userId, data) => api.post(`${API_URL}/users/${userId}/bank_accounts`, { bank_account: data }),
  update: (userId, id, data) => api.patch(`${API_URL}/users/${userId}/bank_accounts/${id}`, { bank_account: data }),
  delete: (userId, id) => api.delete(`${API_URL}/users/${userId}/bank_accounts/${id}`),
  setPrimary: (userId, id) => api.post(`${API_URL}/users/${userId}/bank_accounts/${id}/set_primary`),
};

export const users = {
  list: (params) => api.get(`${API_URL}/users`, { params }),
  get: (id) => api.get(`${API_URL}/users/${id}`),
  update: (id, data) => api.patch(`${API_URL}/users/${id}`, { user: data }),
  invite: (userData) => api.post(`${API_URL}/users/invite`, { user: userData }),
  manageRole: (userId, action, role, reason) => api.post(`${API_URL}/users/${userId}/manage_role`, { role_action: action, role_name: role, reason }),
  assignManager: (userId, managerId) => api.patch(`${API_URL}/users/${userId}/assign_manager`, { manager_id: managerId }),
  subordinates: (userId) => api.get(`${API_URL}/users/${userId}/subordinates`),
  orgChart: () => api.get(`${API_URL}/users/org_chart`),
  delete: (userId, terminationDate) => api.delete(`${API_URL}/users/${userId}`, { params: { termination_date: terminationDate } }),
};

export const leave = {
  getBalances: (userId, year) => api.get(`${API_URL}/users/${userId}/leave_balances`, { params: { year } }),
  myRequests: () => api.get(`${API_URL}/leave_requests`),
  create: (userId, data) => api.post(`${API_URL}/users/${userId}/leave_requests`, { leave_request: data }),
  manage: (id, status, reason) => api.patch(`${API_URL}/leave_requests/${id}/manage_status`, { status, rejection_reason: reason, reason }),
  cancel: (id) => api.patch(`${API_URL}/leave_requests/${id}/manage_status`, { status: "cancelled" }),
  getTypes: () => api.get(`${API_URL}/leave_types`),
  createType: (data) => api.post(`${API_URL}/leave_types`, { leave_type: data }),
  updateType: (id, data) => api.patch(`${API_URL}/leave_types/${id}`, { leave_type: data }),
  deleteType: (id) => api.delete(`${API_URL}/leave_types/${id}`),
  teamRequests: (params) => api.get(`${API_URL}/leave_requests`, { params }),
  update: (id, data) => api.patch(`${API_URL}/leave_requests/${id}`, { leave_request: data }),
};

export const leavePolicies = {
  list: (params) => api.get(`${API_URL}/leave_policies`, { params }),
  get: (id) => api.get(`${API_URL}/leave_policies/${id}`),
  create: (data) => api.post(`${API_URL}/leave_policies`, { leave_policy: data }),
  update: (id, data) => api.patch(`${API_URL}/leave_policies/${id}`, { leave_policy: data }),
  delete: (id) => api.delete(`${API_URL}/leave_policies/${id}`),
};

export const projects = {
  list: (params) => api.get(`${API_URL}/projects`, { params }),
  get: (id) => api.get(`${API_URL}/projects/${id}`),
  create: (data) => api.post(`${API_URL}/projects`, { project: data }),
  update: (id, data) => api.patch(`${API_URL}/projects/${id}`, { project: data }),
  delete: (id, terminationDate) => api.delete(`${API_URL}/projects/${id}`, { params: { termination_date: terminationDate } }),
  // Cover image — multipart upload (separate endpoint so the JSON
  // create/update payloads stay clean). Pair with create() to attach
  // an image after the project record exists.
  uploadCoverImage: (id, file) => {
    const fd = new FormData();
    fd.append('cover_image', file);
    return api.patch(`${API_URL}/projects/${id}/cover_image`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  removeCoverImage: (id) => api.delete(`${API_URL}/projects/${id}/cover_image`),
};

export const projectThreads = {
  list: (projectId) => api.get(`${API_URL}/projects/${projectId}/threads`),
  create: (projectId, data) => api.post(`${API_URL}/projects/${projectId}/threads`, { project_thread: data }),
  update: (projectId, id, data) => api.patch(`${API_URL}/projects/${projectId}/threads/${id}`, { project_thread: data }),
  delete: (projectId, id, terminationDate) => api.delete(`${API_URL}/projects/${projectId}/threads/${id}`, { params: { termination_date: terminationDate } }),
};

export const contributors = {
  list: (projectId, threadId) => api.get(`${API_URL}/projects/${projectId}/threads/${threadId}/contributors`),
  create: (projectId, threadId, data) => api.post(`${API_URL}/projects/${projectId}/threads/${threadId}/contributors`, { contributor: data }),
  update: (projectId, threadId, id, data) => api.patch(`${API_URL}/projects/${projectId}/threads/${threadId}/contributors/${id}`, { contributor: data }),
  delete: (projectId, threadId, id) => api.delete(`${API_URL}/projects/${projectId}/threads/${threadId}/contributors/${id}`),
};

export const threadStatuses = {
  list: () => api.get(`${API_URL}/thread_statuses`),
};

export const invoices = {
  list: (params) => api.get(`${API_URL}/invoices`, { params }),
  get: (id) => api.get(`${API_URL}/invoices/${id}`),
  create: (data) => api.post(`${API_URL}/invoices`, { invoice: data }),
  update: (id, data) => api.patch(`${API_URL}/invoices/${id}`, { invoice: data }),
  delete: (id) => api.delete(`${API_URL}/invoices/${id}`),
};

export const currencyConversions = {
  list: (invoiceId) => api.get(`${API_URL}/invoices/${invoiceId}/currency_conversions`),
  create: (invoiceId, data) => api.post(`${API_URL}/invoices/${invoiceId}/currency_conversions`, { currency_conversion: data }),
  delete: (invoiceId, id) => api.delete(`${API_URL}/invoices/${invoiceId}/currency_conversions/${id}`),
};

export const salaryLogs = {
  list: (params) => api.get(`${API_URL}/salary_logs`, { params }),
  get: (id) => api.get(`${API_URL}/salary_logs/${id}`),
  create: (data) => api.post(`${API_URL}/salary_logs`, { salary_log: data }),
  update: (id, data) => api.patch(`${API_URL}/salary_logs/${id}`, { salary_log: data }),
  delete: (id) => api.delete(`${API_URL}/salary_logs/${id}`),
  payslipUrl: (id, { disposition = 'inline' } = {}) =>
    `${API_URL}/salary_logs/${id}/payslip${disposition ? `?disposition=${disposition}` : ''}`,
  downloadPayslip: (id) =>
    api.get(`${API_URL}/salary_logs/${id}/payslip`, { responseType: 'blob', params: { disposition: 'attachment' } }),
  viewPayslip: (id) =>
    api.get(`${API_URL}/salary_logs/${id}/payslip`, { responseType: 'blob', params: { disposition: 'inline' } }),
  regeneratePayslip: (id) =>
    api.post(`${API_URL}/salary_logs/${id}/regenerate_payslip`),
  bulk: (ids, action) =>
    api.patch(`${API_URL}/salary_logs/bulk`, { salary_log_ids: ids, bulk_action: action }),
};

export const allowanceTypes = {
  list: (params) => api.get(`${API_URL}/allowance_types`, { params }),
  create: (data) => api.post(`${API_URL}/allowance_types`, { allowance_type: data }),
  update: (id, data) => api.patch(`${API_URL}/allowance_types/${id}`, { allowance_type: data }),
  delete: (id) => api.delete(`${API_URL}/allowance_types/${id}`),
};

export const deductionTypes = {
  list: (params) => api.get(`${API_URL}/deduction_types`, { params }),
  create: (data) => api.post(`${API_URL}/deduction_types`, { deduction_type: data }),
  update: (id, data) => api.patch(`${API_URL}/deduction_types/${id}`, { deduction_type: data }),
  delete: (id) => api.delete(`${API_URL}/deduction_types/${id}`),
};

export const payroll = {
  leavePreview: (data) => api.post(`${API_URL}/payroll/leave_preview`, data),
  seed: (data) => api.post(`${API_URL}/payroll/seed`, data),
};

export const payrollRuns = {
  list: (params) => api.get(`${API_URL}/payroll_runs`, { params }),
  get: (id) => api.get(`${API_URL}/payroll_runs/${id}`),
  create: (data) => api.post(`${API_URL}/payroll_runs`, { payroll_run: data }),
  complete: (id, data) => api.patch(`${API_URL}/payroll_runs/${id}/complete`, { payroll_run: data }),
  approveDrafts: (id) => api.patch(`${API_URL}/payroll_runs/${id}/approve_drafts`),
};

export const compensationProfiles = {
  list: (userId) => api.get(`${API_URL}/users/${userId}/compensation_profiles`),
  get: (userId, id) => api.get(`${API_URL}/users/${userId}/compensation_profiles/${id}`),
  create: (userId, data) =>
    api.post(`${API_URL}/users/${userId}/compensation_profiles`, { compensation_profile: data }),
  update: (userId, id, data) =>
    api.patch(`${API_URL}/users/${userId}/compensation_profiles/${id}`, { compensation_profile: data }),
  delete: (userId, id) => api.delete(`${API_URL}/users/${userId}/compensation_profiles/${id}`),
};

export const slackIntegration = {
  status: () => api.get(`${API_URL}/integrations/slack`),
  beginInstall: () => api.get(`${API_URL}/integrations/slack/install`),
  disconnect: () => api.delete(`${API_URL}/integrations/slack`),
  // Save the announcement channel for celebration / broadcast posts.
  // Owner pastes the Slack channel ID (CXXXXXX) and optionally a label.
  updateSettings: (data) =>
    api.patch(`${API_URL}/integrations/slack`, { slack_installation: data }),
};

export const expenseCategories = {
  list: () => api.get(`${API_URL}/expense_categories`),
  create: (data) => api.post(`${API_URL}/expense_categories`, { expense_category: data }),
  update: (id, data) => api.patch(`${API_URL}/expense_categories/${id}`, { expense_category: data }),
  delete: (id) => api.delete(`${API_URL}/expense_categories/${id}`),
};

export const expenses = {
  list: (params) => api.get(`${API_URL}/expenses`, { params }),
  get: (id) => api.get(`${API_URL}/expenses/${id}`),
  create: (data) => api.post(`${API_URL}/expenses`, { expense: data }),
  update: (id, data) => api.patch(`${API_URL}/expenses/${id}`, { expense: data }),
  delete: (id) => api.delete(`${API_URL}/expenses/${id}`),
};

export const reports = {
  financialSummary: (params) => api.get(`${API_URL}/reports/financial_summary`, { params }),
  monthlyTrends: (params) => api.get(`${API_URL}/reports/monthly_trends`, { params }),
  revenueByProject: (params) => api.get(`${API_URL}/reports/revenue_by_project`, { params }),
  expenseBreakdown: (params) => api.get(`${API_URL}/reports/expense_breakdown`, { params }),
  employeeCosts: (params) => api.get(`${API_URL}/reports/employee_costs`, { params }),
  cashFlow: (params) => api.get(`${API_URL}/reports/cash_flow`, { params }),
};

export const roles = {
  list: () => api.get(`${API_URL}/roles`),
  schema: () => api.get(`${API_URL}/roles/schema`),
  create: (data) => api.post(`${API_URL}/roles`, { role: data }),
  update: (id, data) => api.put(`${API_URL}/roles/${id}`, { role: data }),
  delete: (id) => api.delete(`${API_URL}/roles/${id}`),
};
